import React, { useState, useEffect } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import classNames from 'classnames';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/UI.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { loadData, clearNewlyCreatedProduct } from './ConsoleProductCatalogPage.slice';
import {
  PaginationLinks,
  ConsoleWrapper,
  IconSpinner,
  Select,
  Input,
  Button,
  NamedLink,
  CategoryModal
} from '../../components';
import ProductItem from './ProductItem/ProductItem';
import { parse, stringify } from '../../util/urlHelpers';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import config from '../../config';

import css from './ConsoleProductCatalogPage.css';

const countries = [
  { countryId: 'all', country: 'Console.all' },
  ...config.countryNames
];

const ConsoleProductCatalogPageComponent = props => {
  const {
    productCatalog = [],
    fetchInProgress,
    scrollingDisabled,
    fetchProductCatalogListError,
    pagination,
    intl,
    location: { search, pathname },
    history,
    name,
    tab,
    currentUser,
    onManageDisableScrolling,
    onClearNewlyCreatedProduct,
    newlyCreatedProductId
  } = props;
  const parsedSearch = parse(search);
  const { countryId = config.custom.countryId, page, phrase, businessCategory } = parsedSearch;
  const numberOfCategories = businessCategory && businessCategory?.trim().split(',').length;
  const [searchPhrase, setSearchPhrase] = useState(phrase || '');
  const [isExpandedItems, setIsExpandedItems] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [highlightedProduct, setHighlightedProduct] = useState(parsedSearch.highlightedProductId || null);
  const [highlightedItem, setHighlightedItem] = useState(parsedSearch.highlightedItemId || null);

  useEffect(() => {
    if (parsedSearch.highlightedProductId || parsedSearch.highlightedItemId) {
      const cleanParams = { ...parsedSearch };
      delete cleanParams.highlightedProductId;
      delete cleanParams.highlightedItemId;

      history.replace({
        pathname,
        search: stringify(cleanParams),
      });
    }
  }, []);

  const clearHighlights = () => {
    if (highlightedProduct || highlightedItem) {
      setHighlightedProduct(null);
      setHighlightedItem(null);
    }
  };

  const pagingLinks =
    !fetchInProgress && pagination && pagination?.totalPages > 1 ? (
      <PaginationLinks
        className={css.pagination}
        pageName={name}
        pagePathParams={{ tab }}
        pageSearchParams={parse(search)}
        pagination={pagination}
      />
    ) : null;

  const errorMessage = fetchProductCatalogListError ? (
    <p className={css.error}>{fetchProductCatalogListError?.message}</p>
  ) : null;

  const noResultsMessage =
    !fetchInProgress && !productCatalog?.length && !fetchProductCatalogListError ? (
      <p>
        <FormattedMessage id="Console.noProductFound" />
      </p>
    ) : null;

  const handleCountryChange = event => {
    onClearNewlyCreatedProduct();
    const searchCountryId = event.target.value === 'all' ? undefined : event.target.value;

    history.push({
      pathname,
      search: `${stringify({ ...parse(search), countryId: searchCountryId, page: 1 })}`,
    });
  };

  const adminCountries = currentUser?.attributes.isAdmin
    ? countries
    : countries.filter(country =>
      currentUser?.attributes.adminForCountries?.includes(country.countryId)
    );

  const handleChange = event => {
    onClearNewlyCreatedProduct();
    setSearchPhrase(event.target.value);
  };

  const handleSubmit = event => {
    event.preventDefault();
    onClearNewlyCreatedProduct();

    history.push({
      pathname,
      search: `${stringify({ ...parse(search), phrase: searchPhrase, page: 1 })}`,
    });
  };

  const handleExpandClick = event => {
    event.preventDefault();
    clearHighlights();
    setIsExpandedItems(prevState => !prevState);
  };

  const handleCategoriesSubmit = (subcategories) => {
    onClearNewlyCreatedProduct();

    history.push({
      pathname,
      search: `${stringify({
        ...parse(search),
        businessCategory: subcategories,
        page: 1,
      })}`,
    });
  };

  useEffect(() => {
    if (!page) {
      setSearchPhrase('');
      history.push({ pathname, search: `${stringify({ ...parse(search), page: 1 })}` });
    }
  }, [page]);

  return (
    <ConsoleWrapper scrollingDisabled={scrollingDisabled} name={name} tab={tab}>
      <div onClick={clearHighlights}>
      {errorMessage}
      <form onSubmit={handleSubmit} className={css.searchInputWrapper}>
        <Input
          value={searchPhrase || ''}
          onChange={handleChange}
          placeholder={intl.formatMessage({ id: 'Console.searchCatalogProduct' })}
          label={intl.formatMessage({ id: 'Console.search' })}
          isValidation={false}
        />
      </form>
      <div className={css.filterContainer}>
        <div className={css.selectWrapper}>
          <Select
            value={countryId}
            onChange={handleCountryChange}
            label={intl.formatMessage({ id: 'Console.country' })}
            isValidation={false}
          >
            <option hidden value="">
              {intl.formatMessage({ id: 'Console.select' })}
            </option>
            {adminCountries.map(({ countryId, country }) => (
              <option key={countryId} value={countryId}>{intl.formatMessage({ id: country })}</option>
            ))}
          </Select>
          <Button className={classNames(css.btn, css.categories)} onClick={() => setCategoryModalOpen(true)}>
            <FormattedMessage id="ConsoleTransactionPage.category" />
            {numberOfCategories && <span> ({numberOfCategories})</span>}
          </Button>
        </div>
        <div className={css.actionContainer}>
          <NamedLink className={css.link} name="EditProductPage" params={{ id: '00000000-0000-0000-0000-000000000000', type: 'new' }}>
            <FormattedMessage id="Console.addCatalogProduct" />
          </NamedLink>
          <Button className={css.btn} onClick={handleExpandClick}>
            {intl.formatMessage({
              id: isExpandedItems ? 'Console.collapseCatalogItems' : 'Console.expandCatalogItems',
            })}
          </Button>
        </div>
      </div>
      {fetchInProgress ? (
        <div className={css.listItemsLoading}>
          <IconSpinner />
        </div>
      ) : (
        <>
          {!!productCatalog.length && <p className={css.totalItems}>
            {intl.formatMessage({ id: 'Console.numberOfProducts' })}:{' '}
            <span>{pagination.totalItems}</span>
          </p>}
          <ul className={css.list}>
            {productCatalog.map(item => (
              <div key={item.id.uuid} onClick={e => e.stopPropagation()}>
                <ProductItem 
                  key={item.id.uuid}
                  catalogProduct={item} 
                  intl={intl} 
                  isExpandedItems={isExpandedItems} 
                  isHighlighted={item.id.uuid === highlightedProduct && !highlightedItem}
                  highlightedItemId={highlightedItem}
                  onUserInteraction={clearHighlights}
                  isNewlyCreated={item.id.uuid === newlyCreatedProductId}
                />
              </div>
            ))}
          </ul>
        </>
      )}
      {noResultsMessage}
      {pagingLinks}
      <CategoryModal
        isOpen={categoryModalOpen}
        onCloseModal={() => setCategoryModalOpen(false)}
        onManageDisableScrolling={onManageDisableScrolling}
        handleSubmit={handleCategoriesSubmit}
        initBusinessCategory={businessCategory?.trim().split(',')}
        id="CategoryModal"
      />
      </div>
    </ConsoleWrapper>
  );
};

const mapStateToProps = state => {
  const {
    fetchInProgress,
    fetchProductCatalogListError,
    pagination,
    productCatalogList,
    newlyCreatedProductId
  } = state.ConsoleProductCatalogPage;
  const { currentUser } = state.user;
  return {
    fetchInProgress,
    fetchProductCatalogListError,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    productCatalog: getMarketplaceEntities(state, productCatalogList),
    currentUser,
    newlyCreatedProductId
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onClearNewlyCreatedProduct: () => dispatch(clearNewlyCreatedProduct()),
});

const ConsoleProductCatalogPage = compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl,
  withRouter
)(ConsoleProductCatalogPageComponent);

ConsoleProductCatalogPage.loadData = loadData;

export default ConsoleProductCatalogPage;
